import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  ClipboardList,
  Trash2,
  Users,
  DollarSign,
  Camera,
  AlertTriangle,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";

const statusColor = {
  scheduled: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
};
const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", date: "", assigned_cleaner_ids: [], notes: "" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [splitJob, setSplitJob] = useState(null);
  const [splitValues, setSplitValues] = useState({});

  const load = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/assignable").then((r) => setCleaners(r.data));
  }, []);

  const toggleCleaner = (id) =>
    setForm((f) => ({
      ...f,
      assigned_cleaner_ids: f.assigned_cleaner_ids.includes(id)
        ? f.assigned_cleaner_ids.filter((c) => c !== id)
        : [...f.assigned_cleaner_ids, id],
    }));

  const create = async () => {
    if (!form.property_id || !form.date)
      return toast.error("Choose a property and date");
    setSaving(true);
    try {
      await api.post("/jobs", form);
      toast.success("Job created");
      setOpen(false);
      setForm({ property_id: "", date: "", assigned_cleaner_ids: [], notes: "" });
      load();
    } catch (e) {
      toast.error("Could not create job");
    } finally {
      setSaving(false);
    }
  };

  const assign = async (job, cleanerId) => {
    const ids = job.assigned_cleaner_ids.includes(cleanerId)
      ? job.assigned_cleaner_ids.filter((c) => c !== cleanerId)
      : [...job.assigned_cleaner_ids, cleanerId];
    await api.put(`/jobs/${job.id}`, { assigned_cleaner_ids: ids });
    load();
  };

  const setStatus = async (job, status) => {
    await api.put(`/jobs/${job.id}`, { status });
    load();
  };

  const openSplit = (job) => {
    const init = {};
    (job.cleaners || []).forEach((c) => {
      init[c.id] = String(c.amount ?? "");
    });
    setSplitValues(init);
    setSplitJob(job);
  };

  const splitTotal = () =>
    Object.values(splitValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const resetEven = () => {
    if (!splitJob) return;
    const ids = splitJob.assigned_cleaner_ids || [];
    const even = ids.length ? Math.round((splitJob.cleaner_rate / ids.length) * 100) / 100 : 0;
    const next = {};
    ids.forEach((id) => (next[id] = String(even)));
    setSplitValues(next);
  };

  const saveSplit = async () => {
    const payouts = {};
    Object.entries(splitValues).forEach(([id, v]) => {
      payouts[id] = parseFloat(v) || 0;
    });
    await api.put(`/jobs/${splitJob.id}`, { payouts });
    toast.success("Payout split updated");
    setSplitJob(null);
    load();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Could not delete job");
    }
  };

  const filtered = jobs.filter((j) => filter === "all" || j.status === filter);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
            Operations
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Cleaning Jobs</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-job-button">
          <Plus className="h-4 w-4 mr-2" /> New job
        </Button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {["all", "scheduled", "in_progress", "completed"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)}
            className="capitalize"
            data-testid={`filter-${s}`}
          >
            {s.replace("_", " ")}
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">No jobs in this view.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((j) => {
            const damages = j.damage_count;
            return (
              <Card key={j.id} className="p-5" data-testid={`job-${j.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: j.property_color || "#4A7C59" }}
                      />
                      <h3 className="font-display text-lg font-semibold">{j.property_name}</h3>
                      <Badge className={`${statusColor[j.status]} border-0 capitalize`}>
                        {j.status.replace("_", " ")}
                      </Badge>
                      {j.source === "ical" && (
                        <Badge variant="outline" className="text-xs">
                          Auto · turnover
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{fmtDate(j.date)}</p>
                    {j.notes && (
                      <p className="text-sm text-muted-foreground mt-1">{j.notes}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <DollarSign className="h-4 w-4" /> ${j.cost_per_cleaning} charge
                      </span>
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        ${j.cleaner_rate} cleaner payout
                      </span>
                    </div>
                    {j.cleaners?.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {j.cleaners.map((c) => (
                          <span
                            key={c.id}
                            className="inline-flex items-center gap-1 text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-full"
                          >
                            {c.name}: <b>${Number(c.amount).toFixed(2)}</b>
                          </span>
                        ))}
                        {j.custom_split && (
                          <Badge variant="outline" className="text-[10px]">
                            custom split
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => openSplit(j)}
                          data-testid={`edit-split-${j.id}`}
                        >
                          <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Edit pay
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="lg:w-72 space-y-3">
                    <div>
                      <Label className="text-xs flex items-center gap-1 mb-1.5">
                        <Users className="h-3.5 w-3.5" /> Assigned to clean
                      </Label>
                      <div className="flex flex-wrap gap-1.5">
                        {cleaners.length === 0 && (
                          <span className="text-xs text-muted-foreground">
                            Add team members first
                          </span>
                        )}
                        {cleaners.map((c) => {
                          const on = j.assigned_cleaner_ids?.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              onClick={() => assign(j, c.id)}
                              data-testid={`assign-${j.id}-${c.id}`}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                on
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "bg-background text-muted-foreground border-border hover:border-primary"
                              }`}
                            >
                              {c.name}
                              {c.role === "admin" ? " ★" : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Select value={j.status} onValueChange={(v) => setStatus(j, v)}>
                        <SelectTrigger className="h-9 text-sm" data-testid={`status-select-${j.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(j)}
                        data-testid={`delete-job-${j.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* New job dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">New cleaning job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select
                value={form.property_id}
                onValueChange={(v) => setForm({ ...form, property_id: v })}
              >
                <SelectTrigger data-testid="job-property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="job-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Assign cleaners</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto scrollbar-thin">
                {cleaners.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.assigned_cleaner_ids.includes(c.id)}
                      onCheckedChange={() => toggleCleaner(c.id)}
                    />
                    {c.name}
                  </label>
                ))}
                {cleaners.length === 0 && (
                  <p className="text-xs text-muted-foreground">No cleaners yet.</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={saving} data-testid="save-job-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              The cleaning job for "{deleteTarget?.property_name}" on {deleteTarget?.date} will be
              removed, along with its uploaded photos. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-job"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-person payout split editor */}
      <Dialog open={!!splitJob} onOpenChange={(o) => !o && setSplitJob(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Edit cleaner pay</DialogTitle>
          </DialogHeader>
          {splitJob && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Set what each person earns for this {splitJob.property_name} cleaning. Property
                payout is <b>${splitJob.cleaner_rate}</b>.
              </p>
              {(splitJob.cleaners || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Assign someone to this job first.
                </p>
              ) : (
                <div className="space-y-3">
                  {(splitJob.cleaners || []).map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{c.name}</span>
                      <div className="relative w-32">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          $
                        </span>
                        <Input
                          type="number"
                          className="pl-6"
                          value={splitValues[c.id] ?? ""}
                          onChange={(e) =>
                            setSplitValues((v) => ({ ...v, [c.id]: e.target.value }))
                          }
                          data-testid={`split-input-${c.id}`}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t border-border text-sm">
                    <button
                      onClick={resetEven}
                      className="text-primary hover:underline"
                      data-testid="reset-even-split"
                    >
                      Reset to even split
                    </button>
                    <span className="text-muted-foreground">
                      Total: <b className="text-foreground">${splitTotal().toFixed(2)}</b>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitJob(null)}>
              Cancel
            </Button>
            <Button
              onClick={saveSplit}
              disabled={!splitJob?.cleaners?.length}
              data-testid="save-split-button"
            >
              Save pay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
